import {
  createGrid,
  GridOptions,
  ColDef,
  CellClickedEvent,
  CellValueChangedEvent,
  ProcessDataFromClipboardParams,
  UndoEndedEvent,
  IRowNode,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import {
  EB_EDITABLE_HEADER_HEIGHT,
  EB_EDITABLE_ROW_HEIGHT,
  ebEditableTheme,
} from "../../application/toolkit/ag_grid_shared/eb_theme";
import {
  autoFocusFirstCell,
  EXCEL_ENTER_NAVIGATION,
  focusFirstCell,
} from "../../application/toolkit/ag_grid_shared/excel_navigation";
import { ebLocaleText } from "../../application/toolkit/ag_grid_shared/filter_defaults";
import { NO_CLIENT_EXPORT } from "../../application/toolkit/ag_grid_shared/no_client_export";

type GridRow = Record<string, unknown>;

export type InlineRow = Record<string, string | null>;

export type RowLevel = "error" | "warning" | "info" | "ok";

export const TRUNCATED_EVENT = "append-voters-grid:truncated";

export interface GridRowError {
  index: number;
  fields: Record<string, string[]>;
  message: string;
  level?: RowLevel;
}

export const LEVEL_CLASS = (level: RowLevel): string => `append-voters-grid__cell--${level}`;

export const CELL_ERROR_CLASS = LEVEL_CLASS("error");

export const MAX_VISIBLE_ROWS = 20;

export const CELL_ERROR_EVENT = "append-voters-grid:cell-error";

// Edits here never reach the server on their own — Add Voters commits the VotersValidation the
// last Validate click created, so a cell changed afterwards would be committed with its pre-edit
// value. The page controller listens for this and makes the list validate again.
export const CHANGED_EVENT = "append-voters-grid:changed";

// Leading, non-editable "why this row failed" column, shown only once Validate marks
// something. Same job as the voter list's own Errors column
// (VoterInformationLines::ColumnDefsHelpers#base_errors_column) — the reason is read here, as
// text. Nothing on this grid is behind a hover: it never fires on a touch screen.
export const ERRORS_COL_ID = "errors";

export const ERRORS_COLUMN_MIN_WIDTH = 200;

export const NARROW_MIN_ERRORS_COLUMN_WIDTH = 150;

const FALLBACK_VERTICAL_BORDERS = 2;

// One below breakpoint.scss's $screen-tablet, so the grid and the page CSS switch
// layouts at the same width.
export const NARROW_MAX_WIDTH = 639;

export const TOUCH_ROW_HEIGHT = 44;

export const NARROW_ROW_NUMBER_WIDTH = 36;

export const NARROW_MIN_COLUMN_WIDTH = 140;

export const NARROW_MIN_EMAIL_COLUMN_WIDTH = 180;

// Page chrome above and below the grid on a phone: header, picker, panel padding,
// the Validate button and the sticky action bar.
export const NARROW_CHROME_ALLOWANCE = 300;

export const NARROW_MIN_VISIBLE_ROWS = 4;

const RESIZE_DEBOUNCE_MS = 150;

export interface ViewportInfo {
  width: number;
  height: number;
  coarsePointer: boolean;
}

export function isNarrowViewport(viewport: ViewportInfo): boolean {
  return viewport.width <= NARROW_MAX_WIDTH;
}

export function rowHeightFor(viewport: ViewportInfo): number {
  return viewport.coarsePointer ? TOUCH_ROW_HEIGHT : EB_EDITABLE_ROW_HEIGHT;
}

// On a phone the 20-row box is taller than the viewport, which buries the Validate
// button under a grid the admin has to scroll past. Fit the rows to what is left of
// the screen instead, but never below NARROW_MIN_VISIBLE_ROWS.
export function visibleRowsFor(capacity: number, viewport: ViewportInfo, rowHeight: number): number {
  const capped = Math.max(1, Math.min(capacity, MAX_VISIBLE_ROWS));
  if (!isNarrowViewport(viewport)) return capped;

  const fits = Math.floor((viewport.height - NARROW_CHROME_ALLOWANCE) / rowHeight);

  return Math.min(capped, Math.max(NARROW_MIN_VISIBLE_ROWS, fits));
}

// A row that failed validation is taller than rowHeight: the Errors column wraps its reason over
// several lines and AG Grid's autoHeight grows the row to fit it. Sizing the box at
// rowHeight x rows then clips that row to its first line, so measure from the heights AG Grid
// reported and fall back to rowHeight only for the rows it has not measured.
export function boxRowsHeight(
  rowHeights: number[],
  capacity: number,
  viewport: ViewportInfo,
  rowHeight: number,
): number {
  const rows = visibleRowsFor(capacity, viewport, rowHeight);
  const heightAt = (index: number) => rowHeights[index] || rowHeight;
  let total = 0;

  if (!isNarrowViewport(viewport)) {
    for (let index = 0; index < rows; index += 1) total += heightAt(index);

    return total;
  }

  // A phone has only what is left of the screen to spend: keep NARROW_MIN_VISIBLE_ROWS rows
  // whatever they measure, then take a further row only while it still fits.
  const budget = viewport.height - NARROW_CHROME_ALLOWANCE;
  for (let index = 0; index < rows; index += 1) {
    const grown = total + heightAt(index);
    if (index >= NARROW_MIN_VISIBLE_ROWS && grown > budget) break;
    total = grown;
  }

  return total;
}

export function minColumnWidthFor(colId: string | undefined): number {
  return colId && /email/i.test(colId) ? NARROW_MIN_EMAIL_COLUMN_WIDTH : NARROW_MIN_COLUMN_WIDTH;
}

export default class extends AgGridBaseController {
  static values = {
    ...AgGridBaseController.values,
    maxRows: Number,
    statusHeader: { type: String, default: "Errors" },
  };

  declare readonly maxRowsValue: number;
  declare readonly statusHeaderValue: string;

  private lastPasteGrowthNodeIds: string[] | null = null;

  private blankTopUpScheduled = false;

  private fittedRowCount: number | null = null;

  private revealObserver: ResizeObserver | null = null;
  private hasBeenSized = false;

  private rowErrors = new Map<number, GridRowError>();

  private submittedRowIndexes: number[] = [];

  private resizeHandler: (() => void) | null = null;

  private resizeTimer: number | null = null;

  connect() {
    super.connect();

    if (!this.hasGridTarget) return;

    const viewport = this.viewport;
    const narrow = isNarrowViewport(viewport);

    const options: GridOptions = {
      theme: ebEditableTheme,
      localeText: ebLocaleText,
      columnDefs: [this.errorsColumnDef(narrow), ...(narrow ? this.narrowColumnDefs() : this.columnDefsValue)],
      rowData: this.buildBlankRows(this.rowCapacity),
      domLayout: "normal",
      suppressMovableColumns: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowDrag: true,
      rowGroupPanelShow: "never",
      // Drag the handle down a column to repeat a value, the way the Handsontable grid this
      // replaced did — same option the voter list's editor grid uses.
      cellSelection: { handle: { mode: "fill" } },
      // AG Grid fills numbers as a series by default, so dragging an ID of 4 writes 5, 6, 7.
      // A voter list is typed the way a spreadsheet is: the drag repeats what was entered.
      fillOperation: (params) => params.initialValues[params.values.length % params.initialValues.length],
      rowNumbers: narrow
        ? { width: NARROW_ROW_NUMBER_WIDTH, minWidth: NARROW_ROW_NUMBER_WIDTH }
        : { width: 50, minWidth: 50 },
      stopEditingWhenCellsLoseFocus: true,
      ...EXCEL_ENTER_NAVIGATION,
      ...(viewport.coarsePointer
        ? { rowHeight: TOUCH_ROW_HEIGHT, singleClickEdit: true }
        : {}),
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      suppressContextMenu: true,
      processDataFromClipboard: (params: ProcessDataFromClipboardParams) => this.growGridForPaste(params),
      onCellValueChanged: (event: CellValueChangedEvent) => this.handleCellValueChanged(event),
      onCellClicked: (event: CellClickedEvent) => this.handleCellClicked(event),
      onFirstDataRendered: (event) => {
        this.fitColumns();
        autoFocusFirstCell(event.api, this.gridTarget);
      },
      // autoHeight measures a wrapped error reason after the row is rendered and reports the
      // taller row here; the box has to grow with it or the reason stays clipped to one line.
      onModelUpdated: () => this.sizeBoxToRows(),
      onUndoEnded: (event: UndoEndedEvent) => this.handleUndoEnded(event),
      popupParent: document.body,
      defaultColDef: {
        editable: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        cellClassRules: {
          [CELL_ERROR_CLASS]: (params) => this.cellHasError(params.node.rowIndex, params.colDef),
        },
      },
      ...this.gridOptionsValue,
      ...NO_CLIENT_EXPORT,
    };

    this.gridApi = createGrid(this.gridTarget, options);

    this.sizeBoxToRows();

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      this.gridApi?.clearCellSelection();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);

    this.resizeHandler = () => this.scheduleResize();
    window.addEventListener("resize", this.resizeHandler);
    window.addEventListener("orientationchange", this.resizeHandler);

    this.observeReveal();
  }

  disconnect() {
    this.revealObserver?.disconnect();
    this.revealObserver = null;
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      window.removeEventListener("orientationchange", this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.resizeTimer !== null) {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    super.disconnect();
  }

  private get viewport(): ViewportInfo {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      coarsePointer: window.matchMedia?.("(pointer: coarse)")?.matches ?? false,
    };
  }

  // NOT added to `columnDefsValue`: that attribute is what `getColKeys` reads, so keeping this
  // colDef grid-only is what stops `gatherRows` from posting an `errors` field as voter data.
  private errorsColumnDef(narrow: boolean): ColDef {
    return {
      colId: ERRORS_COL_ID,
      headerName: this.statusHeaderValue,
      editable: false,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      // AG Grid's paste walks displayed columns rightward from the focused cell and consumes a
      // position even where it cannot write, so a leading column the caret can reach would eat the
      // first pasted value. These keep the caret out; `growGridForPaste` catches a click-focus.
      suppressNavigable: true,
      suppressPaste: true,
      lockPosition: "left",
      hide: true,
      wrapText: true,
      autoHeight: true,
      minWidth: narrow ? NARROW_MIN_ERRORS_COLUMN_WIDTH : ERRORS_COLUMN_MIN_WIDTH,
      valueGetter: (params) => this.rowErrorMessage(params.node?.rowIndex),
      cellClass: (params) => {
        const level = this.rowErrorLevel(params.node?.rowIndex);
        return level ? LEVEL_CLASS(level) : "";
      },
    };
  }

  private rowErrorLevel(rowIndex: number | null | undefined): RowLevel | null {
    if (rowIndex === null || rowIndex === undefined) return null;

    const rowError = this.rowErrors.get(rowIndex);
    if (!rowError) return null;

    return rowError.level ?? "error";
  }

  private rowErrorMessage(rowIndex: number | null | undefined): string {
    if (rowIndex === null || rowIndex === undefined) return "";

    return this.rowErrors.get(rowIndex)?.message ?? "";
  }

  private narrowColumnDefs(): ColDef[] {
    return this.columnDefsValue.map((colDef) => ({
      minWidth: minColumnWidthFor(this.resolveColId(colDef)),
      ...colDef,
    }));
  }

  // The Errors column is the reason's home, but a phone scrolls it out of view as soon as the
  // admin reaches a column further right — so a tap on a marked cell hands that cell's reason to
  // the page controller, which has the only status line always on screen. There is no hover
  // anywhere on this grid: it never fires on a touch screen, which is the whole problem.
  private handleCellClicked(event: CellClickedEvent): void {
    if (!this.viewport.coarsePointer) return;

    const message = this.cellErrorMessage(event.node?.rowIndex, event.colDef);
    if (!message) return;

    this.element.dispatchEvent(
      new CustomEvent(CELL_ERROR_EVENT, { bubbles: true, detail: { message } }),
    );
  }

  private scheduleResize(): void {
    if (this.resizeTimer !== null) window.clearTimeout(this.resizeTimer);

    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = null;
      // Fit first: the columns decide the horizontal scrollbar, and the box is measured around it.
      this.fitColumns();
    }, RESIZE_DEBOUNCE_MS);
  }

  switchToBasic(): void {}

  switchToAdvanced(): void {}

  showRowErrors(rowErrors: GridRowError[]): void {
    this.rowErrors = new Map();
    rowErrors.forEach((rowError) => {
      const gridIndex = this.gridRowIndexFor(rowError.index);
      if (gridIndex !== null) this.rowErrors.set(gridIndex, rowError);
    });
    this.setErrorsColumnVisible(this.rowErrors.size > 0);
    this.repaintErrors();
  }

  private gridRowIndexFor(submittedIndex: number): number | null {
    if (this.submittedRowIndexes.length === 0) return submittedIndex;

    const gridIndex = this.submittedRowIndexes[submittedIndex];

    return gridIndex === undefined ? null : gridIndex;
  }

  clearRowErrors(): void {
    if (this.rowErrors.size === 0) return;

    this.rowErrors = new Map();
    this.setErrorsColumnVisible(false);
    this.repaintErrors();
  }

  fitToRows(count: number): void {
    const rows = Math.max(1, Math.min(this.rowCapacity, Math.trunc(count) + 1));

    this.fittedRowCount = rows;
    this.sizeBoxToRows();
  }

  restoreDefaultSize(): void {
    if (this.fittedRowCount === null) return;

    this.fittedRowCount = null;
    this.sizeBoxToRows();
  }

  focusFirstCell(): void {
    if (this.gridApi) focusFirstCell(this.gridApi);
  }

  // Set once here rather than folded into a colDef rebuild the way
  // VoterList::EditorGridComponent's controller has to do it — this grid builds its columnDefs
  // once, in connect(), and never rebuilds them, so there is no rebuild to survive.
  private setErrorsColumnVisible(visible: boolean): void {
    if (!this.gridApi) return;

    this.gridApi.setColumnsVisible([ERRORS_COL_ID], visible);
    // A column appearing mid-session changes the total width; on a wide viewport that has to
    // re-fit or the data columns overflow. No-ops on a phone, which scrolls horizontally anyway.
    this.fitColumns();
  }

  private repaintErrors(): void {
    this.gridApi?.redrawRows();
  }

  private cellHasError(rowIndex: number | null | undefined, colDef: ColDef): boolean {
    return this.rowErrorLevel(rowIndex) === "error" && this.cellErrorMessages(rowIndex, colDef).length > 0;
  }

  private cellErrorMessage(rowIndex: number | null | undefined, colDef: ColDef): string {
    return this.cellErrorMessages(rowIndex, colDef).join("; ");
  }

  private cellErrorMessages(rowIndex: number | null | undefined, colDef: ColDef): string[] {
    if (rowIndex === null || rowIndex === undefined) return [];

    const rowError = this.rowErrors.get(rowIndex);
    if (!rowError) return [];

    const colId = this.resolveColId(colDef);
    if (colId && rowError.fields[colId]) return rowError.fields[colId];

    return this.namesNoRenderedColumn(rowError) ? [rowError.message] : [];
  }

  private namesNoRenderedColumn(rowError: GridRowError): boolean {
    const colKeys = this.getColKeys();

    return !Object.keys(rowError.fields).some((field) => colKeys.includes(field));
  }

  gatherRows(): InlineRow[] {
    const colKeys = this.getColKeys();
    const raw: Array<{ gridIndex: number; row: GridRow }> = [];
    this.gridApi?.forEachNode((node) => {
      if (node.data) raw.push({ gridIndex: node.rowIndex ?? raw.length, row: node.data as GridRow });
    });

    const submitted = raw
      .slice(0, Math.max(0, this.maxRowsValue))
      .filter(({ row }) => this.rowHasContent(row));

    this.submittedRowIndexes = submitted.map(({ gridIndex }) => gridIndex);

    return submitted.map(({ row }) => {
      const obj: InlineRow = {};
      colKeys.forEach((key) => {
        const value = row[key];
        obj[key] = value !== null && value !== undefined ? String(value) : null;
      });
      return obj;
    });
  }

  loadRows(values: string[]): void {
    if (!this.gridApi) return;

    const firstColKey = this.getColKeys()[0];
    if (!firstColKey) return;

    const rows = values
      .slice(0, Math.max(0, this.maxRowsValue))
      .map((value) => this.buildLoadedRow(firstColKey, value));

    if (rows.length < this.maxRowsValue) rows.push(...this.buildBlankRows(1));
    if (rows.length === 0) rows.push(...this.buildBlankRows(Math.min(1, this.maxRowsValue)));

    this.submittedRowIndexes = [];
    this.clearRowErrors();
    this.gridApi.setGridOption("rowData", rows);
    this.sizeBoxToRows();
  }

  // Writes a server-side default back into the typed rows (a blank weight becomes 1 on Validate,
  // as on the Setup page). Only rows that already hold content: a default alone must not turn a
  // spare row into a voter. applyTransaction(update) fires no cell-change, so the pass stays valid.
  fillBlankCells(colKey: string, value: string): void {
    if (!this.gridApi || !this.getColKeys().includes(colKey)) return;

    const update: GridRow[] = [];
    this.gridApi.forEachNode((node) => {
      const row = node.data as GridRow | undefined;
      if (!row || (node.rowIndex ?? 0) >= Math.max(0, this.maxRowsValue)) return;
      if (!this.rowHasContent(row)) return;

      const current = row[colKey];
      if (current !== null && current !== undefined && current !== "") return;

      row[colKey] = value;
      update.push(row);
    });

    if (update.length) this.gridApi.applyTransaction({ update });
  }

  private rowHasContent(row: GridRow): boolean {
    return this.getColKeys().some((key) => {
      const value = row[key];
      return value !== null && value !== undefined && value !== "";
    });
  }

  private getColKeys(): string[] {
    return this.columnDefsValue
      .map((colDef: ColDef) => this.resolveColId(colDef))
      .filter((colId): colId is string => Boolean(colId));
  }

  private resolveColId(colDef: ColDef): string | undefined {
    return colDef.colId ?? (typeof colDef.field === "string" ? colDef.field.split(".")[0] : undefined);
  }

  private get rowCapacity(): number {
    return Math.max(0, Math.trunc(this.maxRowsValue || 0));
  }

  private sizeBoxToRows(): void {
    const viewport = this.viewport;
    const rowsHeight = boxRowsHeight(
      this.measuredRowHeights(), this.boxRowCapacity(), viewport, rowHeightFor(viewport),
    );
    const height =
      `${EB_EDITABLE_HEADER_HEIGHT + rowsHeight + this.horizontalScrollHeight() + this.verticalBorders()}px`;
    // onModelUpdated fires on every transaction, not just a height change.
    if (this.gridTarget.style.height === height) return;

    this.gridTarget.style.height = height;
  }

  // Only the rows the box can show: anything past that is scrolled to, not sized for.
  private measuredRowHeights(): number[] {
    const api = this.gridApi;
    if (!api) return [];

    const count = Math.min(api.getDisplayedRowCount(), this.boxRowCapacity(), MAX_VISIBLE_ROWS);
    const heights: number[] = [];
    for (let index = 0; index < count; index += 1) {
      heights.push(api.getDisplayedRowAtIndex(index)?.rowHeight ?? 0);
    }

    return heights;
  }

  // AG Grid stacks its horizontal scrollbar under the rows, inside the height this box sets — so a
  // box measured as header + rows hands that strip a row's own space, the rows scroll to make room
  // and the one at the edge is cut in half. An overlay scrollbar is taken out of flow and measures
  // 0, which is the right answer for it.
  private horizontalScrollHeight(): number {
    const strip = this.gridTarget.querySelector<HTMLElement>(".ag-body-horizontal-scroll");

    return strip?.offsetHeight ?? 0;
  }

  private verticalBorders(): number {
    const wrapper = this.gridTarget.querySelector(".ag-root-wrapper");
    const style = wrapper && window.getComputedStyle?.(wrapper);
    if (!style) return FALLBACK_VERTICAL_BORDERS;

    const top = parseFloat(style.borderTopWidth);
    const bottom = parseFloat(style.borderBottomWidth);
    if (Number.isNaN(top) || Number.isNaN(bottom)) return FALLBACK_VERTICAL_BORDERS;

    return top + bottom;
  }

  private boxRowCapacity(): number {
    return this.fittedRowCount ?? this.rowCapacity;
  }

  private buildBlankRows(count: number): GridRow[] {
    return Array.from({ length: Math.max(count, 0) }, () => this.buildRow());
  }

  private buildLoadedRow(firstColKey: string, value: string): GridRow {
    const row = this.buildRow();
    row[firstColKey] = value;
    return row;
  }

  private buildRow(): GridRow {
    const row: GridRow = {};
    this.getColKeys().forEach((key) => {
      row[key] = null;
    });
    return row;
  }

  private growGridForPaste(params: ProcessDataFromClipboardParams): string[][] | null {
    if (!this.gridApi) return params.data;
    let focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) return params.data;

    // A click can still land the caret on the Errors column even though arrow keys skip it;
    // starting a paste there would drop its first column, so move to the first data column.
    if (focusedCell.column.getColId() === ERRORS_COL_ID) {
      const firstDataColId = this.getColKeys()[0];
      if (!firstDataColId) return null;

      this.gridApi.setFocusedCell(focusedCell.rowIndex, firstDataColId);
      focusedCell = this.gridApi.getFocusedCell() ?? focusedCell;
    }

    this.lastPasteGrowthNodeIds = null;

    const data = this.trimTrailingBlankLines(params.data);
    if (data.length === 0) return null;

    const totalRows = this.gridApi.getDisplayedRowCount();
    const rowsNeeded = focusedCell.rowIndex + data.length - totalRows;
    if (rowsNeeded <= 0) return data;

    const capacityRemaining = this.maxRowsValue - totalRows;
    const rowsToAdd = Math.min(rowsNeeded, capacityRemaining);
    if (rowsToAdd > 0) {
      const result = this.gridApi.applyTransaction({ add: this.buildBlankRows(rowsToAdd) });
      this.lastPasteGrowthNodeIds = (result?.add ?? [])
        .map((node) => node.id)
        .filter((id): id is string => id !== undefined);
    }

    if (rowsNeeded <= capacityRemaining) return data;

    this.announceTruncation();
    const allowedRows = this.maxRowsValue - focusedCell.rowIndex;
    return allowedRows > 0 ? data.slice(0, allowedRows) : null;
  }

  private trimTrailingBlankLines(data: string[][]): string[][] {
    let end = data.length;
    while (end > 0 && data[end - 1].every((cell) => (cell ?? "") === "")) end -= 1;
    return data.slice(0, end);
  }

  private announceTruncation(): void {
    this.element.dispatchEvent(
      new CustomEvent(TRUNCATED_EVENT, {
        bubbles: true,
        detail: { maxRows: this.maxRowsValue },
      })
    );
  }

  private handleUndoEnded(event: UndoEndedEvent): void {
    if (!event.operationPerformed) return;

    const nodeIds = this.lastPasteGrowthNodeIds;
    this.lastPasteGrowthNodeIds = null;
    if (!nodeIds || !this.gridApi) return;

    const nodes = nodeIds
      .map((id) => this.gridApi!.getRowNode(id))
      .filter((node): node is IRowNode<GridRow> => !!node);

    if (nodes.length !== nodeIds.length) return;
    if (nodes.some((node) => !node.data || this.rowHasContent(node.data))) return;

    this.gridApi.applyTransaction({ remove: nodes.map((node) => node.data!) });
  }

  private handleCellValueChanged(_event: CellValueChangedEvent): void {
    this.scheduleBlankTopUp();
    this.element.dispatchEvent(new CustomEvent(CHANGED_EVENT, { bubbles: true }));
  }

  private scheduleBlankTopUp(): void {
    if (this.blankTopUpScheduled) return;
    this.blankTopUpScheduled = true;
    Promise.resolve().then(() => {
      this.blankTopUpScheduled = false;
      this.appendBlankRowIfLastRowFilled();
    });
  }

  private appendBlankRowIfLastRowFilled(): void {
    if (!this.gridApi) return;

    const totalRows = this.gridApi.getDisplayedRowCount();
    if (totalRows === 0 || totalRows >= this.maxRowsValue) return;

    const lastNode = this.gridApi.getDisplayedRowAtIndex(totalRows - 1);
    if (!lastNode?.data) return;
    if (!this.rowHasContent(lastNode.data as GridRow)) return;

    this.gridApi.applyTransaction({ add: this.buildBlankRows(1) });
  }

  private observeReveal(): void {
    if (typeof ResizeObserver === "undefined") return;

    this.revealObserver = new ResizeObserver((entries) => {
      const visible = entries.some((entry) => entry.contentRect.width > 0);
      if (!visible) {
        this.hasBeenSized = false;
        return;
      }
      if (this.hasBeenSized) return;
      this.hasBeenSized = true;
      this.fitColumns();
    });
    this.revealObserver.observe(this.gridTarget);
  }

  private fitColumns(): void {
    if (!isNarrowViewport(this.viewport)) this.gridApi?.sizeColumnsToFit();

    // Fitting decides whether the columns overflow at all, and that is what puts the horizontal
    // scrollbar under the rows — so the box is measured again after every fit, not only when the
    // row model changes.
    this.sizeBoxToRows();
  }
}
